// ─────────────────────────────────────────────────────────────────────────────
// DocumentsModule — MIDNIGHT elevation
//
// §10 glass nesting: ModulePanel = ds-tile-inset (flat rgba, NO backdrop-filter).
//
// Lists WorkDocuments for the current Work record.
// Type badge palette follows the phase (lib/phaseTone): peach=quote,
// butter=invoice, mint=receipt.
// "View PDF" ghost button with ExternalLink icon; disabled when pdfUrl is empty.
//
// API endpoints:
//   GET /api/invoices/:invoiceId/documents  (when invoiceId present)
//   GET /api/jobs/:jobId/documents          (fallback when only jobId present)
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import { FileText, ExternalLink } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { resolveApiUrl } from '@/lib/apiUrl'
import { humanError } from '@/lib/errors'
import type { ApiWorkDocument } from '@/lib/apiTypes'
import { PHASE_TONE, type PhaseTone } from '@/lib/phaseTone'
import type { Record_ } from './types'
import { SectionHeader, ModulePanel } from './_drawerHelpers'
import { formatDate } from '@/utils/formatting'

// ─── Badge styles per document type ──────────────────────────────────────────

// The .tn-* class on the badge sets --c (pastel) and --c-t (low-alpha tint).
const TYPE_BADGE: Record<ApiWorkDocument['type'], { tone: PhaseTone; label: string }> = {
  quote:   { tone: PHASE_TONE.quote,   label: 'Quote' },
  invoice: { tone: PHASE_TONE.billing, label: 'Invoice' },
  receipt: { tone: PHASE_TONE.paid,    label: 'Receipt' },
}

// ─── DocumentsModule ──────────────────────────────────────────────────────────

interface Props {
  record: Record_
}

export function DocumentsModule({ record }: Props) {
  const [docs, setDocs] = useState<ApiWorkDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const { invoiceId, jobId, quoteId } = record
    const endpoint = invoiceId
      ? `/api/invoices/${invoiceId}/documents`
      : jobId
        ? `/api/jobs/${jobId}/documents`
        : quoteId
          ? `/api/quotes/${quoteId}/documents`
          : null

    if (!endpoint) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    apiClient
      .get<{ success: boolean; data: { items: ApiWorkDocument[]; total: number } }>(endpoint)
      .then((res) => {
        const items = res.data?.data?.items ?? []
        // newest first
        setDocs([...items].sort((a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime()))
      })
      .catch((err: unknown) => setError(humanError(err, 'load the documents')))
      .finally(() => setLoading(false))
  }, [record.invoiceId, record.jobId, record.quoteId])

  return (
    <ModulePanel accent="neutral">
      <SectionHeader
        tone="neutral"
        icon={FileText}
        title="Documents"
        meta={!loading && !error ? `${docs.length}` : undefined}
      />

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1, 2].map((i) => (
            <div
              key={i}
              style={{
                height: 44,
                borderRadius: 12,
                background: 'rgb(var(--fg-rgb) / 0.04)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          ))}
        </div>
      ) : error ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--blush, var(--blush))' }}>{error}</p>
      ) : docs.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.40))' }}>
          No documents yet
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {docs.map((doc) => {
            const badge = TYPE_BADGE[doc.type]
            const hasPdf = Boolean(doc.pdfUrl)
            return (
              <div
                key={doc.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 12,
                  background: 'rgb(var(--fg-rgb) / 0.04)',
                  border: '0.5px solid rgb(var(--fg-rgb) / 0.06)',
                }}
              >
                {/* Left: badge + version + date */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span
                    className={`tn-${badge.tone}`}
                    style={{
                      padding: '2px 8px',
                      borderRadius: 9999,
                      fontSize: 12,
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      background: 'var(--c-t)',
                      color: 'var(--c)',
                      flexShrink: 0,
                    }}
                  >
                    {badge.label}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))',
                      flexShrink: 0,
                    }}
                  >
                    v{doc.version}
                  </span>
                  <span
                    style={{
                      fontSize: 12,
                      color: 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.40))',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatDate(doc.generatedAt)}
                  </span>
                </div>

                {/* Right: View PDF ghost button */}
                <a
                  href={hasPdf ? resolveApiUrl(doc.pdfUrl) : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!hasPdf}
                  onClick={!hasPdf ? (e) => e.preventDefault() : undefined}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '5px 10px',
                    borderRadius: 9999,
                    fontSize: 12,
                    fontWeight: 500,
                    textDecoration: 'none',
                    flexShrink: 0,
                    cursor: hasPdf ? 'pointer' : 'not-allowed',
                    color: hasPdf
                      ? 'var(--midnight-text2, rgb(var(--fg-rgb) / 0.60))'
                      : 'var(--midnight-text3, rgb(var(--fg-rgb) / 0.30))',
                    background: 'rgb(var(--fg-rgb) / 0.06)',
                    border: '0.5px solid rgb(var(--fg-rgb) / 0.10)',
                    opacity: hasPdf ? 1 : 0.5,
                  }}
                >
                  <ExternalLink style={{ width: 12, height: 12, flexShrink: 0 }} />
                  View PDF
                </a>
              </div>
            )
          })}
        </div>
      )}
    </ModulePanel>
  )
}
